import { useContext, useEffect, useMemo } from "react"
import { Outlet, useLoaderData, useNavigation } from "react-router"
import { LoadingFullScreen } from "source/shared/components"
import { HelmetProvider } from "@dr.pogodin/react-helmet"
import { SetOrganizationTimeZone } from "source/SetOrganizationTimeZone"
import { WebBoot } from "source/publishing/WebBoot"
import { useChurchCenterPageAnalytics } from "source/shared/hooks/useChurchCenterPageAnalytics"
import { useEnv } from "source/shared/hooks/useEnv"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { getLocalWebBootState } from "source/publishing/WebBoot/webBootState"
import { RedirectRequestErrorBoundary } from "source/shared/routingUtils"
import {
  queryClient,
  ReactQueryProvider,
  RouteDataHydrationBoundary,
} from "source/ReactQueryProvider"
import { DynamicSessionProvider } from "source/shared/contexts/DynamicSession"
import ScrollToTopOnRouteChange from "source/shared/ScrollToTopOnRouteChange"
import { Layout } from "source/Layout"
import { ServerRenderedProps } from "./shared/contexts/ServerRenderedProps"
import * as Sentry from "@sentry/react-router"
import { useSession } from "source/shared/hooks/useSession"
import { GoogleTagManager } from "source/shared/GoogleTagManager"
import { SmartBanner } from "source/shared/SmartBanner"
import { InitializePendo } from "source/shared/InitializePendo"
import { StaffTopBar } from "source/shared/StaffTopBar"
import { BridgeAlertModalProvider } from "source/shared/hooks/useBridgeAlert/BridgeAlertModalProvider"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { AuthorizationToken } from "./authorizationToken.server"
import { webBootQueryOptions } from "./publishing/WebBoot/useWebBootQuery"
import { OriginContext } from "./shared/contexts/OriginContext"
import { HelmetServerStateContext } from "./shared/contexts/HelmetServerState"

export async function clientLoader({ request }: { request: Request }) {
  const { origin, hostname, host, protocol, port } = new URL(request.url)
  const originData = {
    origin,
    hostname,
    host,
    protocol,
    port,
  }

  let webBootState = getLocalWebBootState()
  let webBootStateIsFresh = false

  if (!webBootState) {
    webBootState = await queryClient.ensureQueryData(webBootQueryOptions)
    webBootStateIsFresh = true
  }

  return {
    props: window.serverRenderedProps!,
    authorizationToken: null as null | AuthorizationToken,
    originData,
    webBootState,
    webBootStateIsFresh,
  }
}

export function shouldRevalidate() {
  return false
}

export { LayoutlessErrorBoundary as ErrorBoundary } from "./CommonErrorBoundary"

export default function RootLayout() {
  const {
    props: serverRenderedProps,
    authorizationToken,
    originData,
    webBootState,
    webBootStateIsFresh,
  } = useLoaderData<typeof clientLoader>()
  const onHelmetServerState = useContext(HelmetServerStateContext)

  // we want to setAuthorizationToken once before the first render.
  // useEffect would execute during the commit phase, which is after the first render.
  useMemo(() => {
    if (authorizationToken) {
      sessionApiClient.setAuthorizationToken({ data: authorizationToken })
    }
  }, [authorizationToken])

  return (
    <ServerRenderedProps.Provider value={serverRenderedProps}>
      <OriginContext.Provider value={originData}>
        <HelmetProvider onServerState={onHelmetServerState}>
          <StaffTopBarWrapper />
          <SmartBanner />
          <GoogleTagManager />
          <InitializePendo />
          <RedirectRequestErrorBoundary>
            <ReactQueryProvider>
              <RouteDataHydrationBoundary>
                <DynamicSessionProvider>
                  <SetCurrentUserInSentry />
                  <WebBoot
                    initialState={webBootState}
                    initialStateIsFresh={webBootStateIsFresh}
                  >
                    <SetOrganizationTimeZone>
                      <MonitoringAndAnalytics>
                        <Layout>
                          <RouteLoadingIndicator />
                          <ScrollToTopOnRouteChange>
                            <BridgeAlertModalProvider>
                              <Outlet />
                            </BridgeAlertModalProvider>
                          </ScrollToTopOnRouteChange>
                        </Layout>
                      </MonitoringAndAnalytics>
                    </SetOrganizationTimeZone>
                  </WebBoot>
                  <ReactQueryDevtools />
                </DynamicSessionProvider>
              </RouteDataHydrationBoundary>
            </ReactQueryProvider>
          </RedirectRequestErrorBoundary>
        </HelmetProvider>
      </OriginContext.Provider>
    </ServerRenderedProps.Provider>
  )
}

function RouteLoadingIndicator() {
  const navigation = useNavigation()
  const loading = navigation.state !== "idle"

  useEffect(() => {
    if (window.ReactNativeWebView && !loading)
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: "booted" }))
  }, [loading])

  return loading ? <LoadingFullScreen /> : null
}

function MonitoringAndAnalytics({ children }: { children: React.ReactNode }) {
  const env = useEnv()
  const {
    data: { id: currentUserId },
  } = useSession(false)
  const { current_organization } = useContext(ServerRenderedProps)

  useEffect(() => {
    if (env) {
      import("source/shared/datadogRum").then(
        ({
          startDatadogRealUserMonitoring,
          setDatadogRumUser,
          setDatadogRumOrganization,
        }) => {
          startDatadogRealUserMonitoring(env)
          setDatadogRumUser(currentUserId)
          setDatadogRumOrganization(current_organization?.id)
        },
      )
      import("source/shared/datadogLogs").then(
        ({ startDatadogBrowserLogs, setDatadogLogsUser }) => {
          startDatadogBrowserLogs(env)
          setDatadogLogsUser(currentUserId)
        },
      )
    }
  }, [env, currentUserId, current_organization])

  useChurchCenterPageAnalytics()

  return children
}

function SetCurrentUserInSentry() {
  const {
    current_organization: { id: organizationId },
  } = useContext(ServerRenderedProps)
  const {
    data: { id: currentUserId },
  } = useSession(false)

  useEffect(() => {
    Sentry.setUser({
      id: currentUserId ?? undefined,
      organizationId,
    })
  }, [currentUserId, organizationId])

  return null
}

export function StaffTopBarWrapper() {
  const { current_organization, current_person, staff } =
    useContext(ServerRenderedProps)

  if (!staff?.impersonating) return null

  const personAttributes = current_person.data.attributes
  const personId = current_person.data.id
  const personName =
    personAttributes.name === "Unknown" ? "" : personAttributes.name

  return (
    <StaffTopBar
      personName={personName}
      personId={personId}
      organizationName={current_organization.name}
    />
  )
}
